import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Tabs, Tab, TextField, Table, TableHead, TableBody, TableRow,
  TableCell, TableSortLabel, TableContainer, Typography, Chip,
  CircularProgress, InputAdornment, Autocomplete, Button, Stack,
  TablePagination,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import api from '../../../../services/api';
import { useTopologyBase } from './topologyApi';
import TableSummary from './TableSummary';
import HostDossier from './HostDossier';
import ColumnFilter from './ColumnFilter';

const isNum = (v) => v !== '' && v !== null && v !== undefined && !Number.isNaN(Number(v));
const vazio = (v) => v === '' || v === '—' || v == null;

// Quantos chips de host aparecem antes de colapsar em "+N".
const MAX_CHIPS = 12;

/**
 * Explorador de tabelas.
 *
 * Consome três endpoints, e a divisão é o ponto:
 *
 *   /tables              metadados — colunas, total, indicadores e facetas.
 *                        39 KB, uma vez por empresa.
 *   /tables/{key}        a página visível, já filtrada e ordenada no servidor.
 *   /tables/{key}/facet  valores de uma coluna, sob demanda.
 *
 * Antes tudo vinha numa resposta só — 2,28 MB na TCHE pra mostrar 30 linhas,
 * e todo filtro, ordenação e paginação acontecia no navegador em cima desse
 * bloco. Agora o navegador nunca segura mais que uma página.
 */
export default function TablesPanel({ }) {
  const base = useTopologyBase();
  const [tabs, setTabs] = useState([]);
  const [carregandoMeta, setCarregandoMeta] = useState(false);
  const [carregandoPag, setCarregandoPag] = useState(false);
  const [cur, setCur] = useState(0);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(0);
  const [porPagina, setPorPagina] = useState(100);

  const [busca, setBusca] = useState('');
  const [buscaAplicada, setBuscaAplicada] = useState('');
  const [filtrosCol, setFiltrosCol] = useState({});   // col -> filtro
  const [kpiFiltro, setKpiFiltro] = useState(null);
  const [hostsSel, setHostsSel] = useState([]);
  const [verTodosHosts, setVerTodosHosts] = useState(false);

  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [rowSel, setRowSel] = useState(null);
  const [todosHosts, setTodosHosts] = useState([]);

  const tab = tabs[cur];

  // ---- metadados ----
  useEffect(() => {
    let vivo = true;
    if (!base) return undefined;
    setCarregandoMeta(true);
    api.get(`${base}/tables`)
      .then((res) => {
        if (!vivo) return;
        setTabs(res.data?.tabs || []);
        setTodosHosts(res.data?.hosts || []);
        setCur(0);
        setKpiFiltro(null);
        setHostsSel([]);
        setVerTodosHosts(false);
        setFiltrosCol({});
        setBusca(''); setBuscaAplicada('');
      })
      .catch(() => vivo && setTabs([]))
      .finally(() => vivo && setCarregandoMeta(false));
    return () => { vivo = false; };
  }, [base]);

  // Todos os filtros ativos viram uma lista só pro servidor.
  const filtros = useMemo(() => {
    const out = [];
    if (kpiFiltro) {
      out.push(typeof kpiFiltro === 'string'
        ? { col: null, op: 'contains', value: kpiFiltro }
        : kpiFiltro);
    }
    if (buscaAplicada) out.push({ col: null, op: 'contains', value: buscaAplicada });
    if (hostsSel.length && tab?.host_col != null) {
      // a aba de VLANs guarda a lista inteira numa célula, então é "contém"
      // qualquer um dos selecionados — o servidor não tem OR, então mandamos
      // como `in` só quando a coluna é o nome puro do equipamento
      out.push({ col: tab.host_col, op: tab.host_col === 0 ? 'in' : 'contains',
        value: tab.host_col === 0 ? hostsSel : hostsSel[0] });
    }
    Object.values(filtrosCol).forEach((f) => f && out.push(f));
    return out;
  }, [kpiFiltro, buscaAplicada, hostsSel, filtrosCol, tab]);

  // ---- página ----
  useEffect(() => {
    let vivo = true;
    if (!base || !tab) { setRows([]); setTotal(0); return undefined; }
    setCarregandoPag(true);
    const p = new URLSearchParams({
      offset: String(pagina * porPagina),
      limite: String(porPagina),
      direcao: sortDir,
    });
    if (sortCol != null) p.set('sort', String(sortCol));
    if (filtros.length) p.set('filtros', JSON.stringify(filtros));
    api.get(`${base}/tables/${tab.key}?${p}`)
      .then((res) => {
        if (!vivo) return;
        setRows(res.data?.rows || []);
        setTotal(res.data?.total || 0);
      })
      .catch(() => { if (vivo) { setRows([]); setTotal(0); } })
      .finally(() => vivo && setCarregandoPag(false));
    return () => { vivo = false; };
  }, [base, tab, pagina, porPagina, sortCol, sortDir, filtros]);

  // Filtro novo devolve pra primeira página: continuar na página 7 de um
  // resultado que agora tem 2 linhas mostra uma tabela vazia sem explicação.
  useEffect(() => { setPagina(0); }, [filtros, tab]);

  const buscarFaceta = useCallback((col, q) => (
    api.get(`${base}/tables/${tab.key}/facet?col=${col}&q=${encodeURIComponent(q || '')}`)
      .then((res) => res.data?.valores || [])
      .catch(() => [])
  ), [base, tab]);

  const onSort = (i) => {
    if (sortCol === i) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortCol(i); setSortDir('asc'); }
  };

  const limparTudo = () => {
    setBusca(''); setBuscaAplicada(''); setKpiFiltro(null);
    setHostsSel([]); setVerTodosHosts(false); setFiltrosCol({});
  };
  const temFiltro = !!(buscaAplicada || kpiFiltro || hostsSel.length
    || Object.values(filtrosCol).some(Boolean));

  if (carregandoMeta) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  if (!tabs.length) {
    return (
      <Typography sx={{ p: 2 }} color="text.secondary" variant="body2">
        Sem dados de tabela para esta empresa.
      </Typography>
    );
  }

  return (
    <Paper sx={{
      height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden',
      bgcolor: '#ffffff', color: '#0f172a',
      '& .MuiTypography-root': { color: '#0f172a' },
      '& .MuiInputBase-root': { bgcolor: '#ffffff' },
      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#cbd5e1' },
      '& .MuiInputLabel-root': { color: '#475569' },
      '& .MuiSvgIcon-root': { color: '#475569' },
      '& .MuiTableCell-root': { borderColor: '#e2e8f0' },
    }}>
      <Tabs
        value={cur}
        onChange={(_, v) => {
          setCur(v);
          setSortCol(null); setSortDir('asc');
          setBusca(''); setBuscaAplicada('');
          // filtro de coluna e de KPI são por índice de coluna: não
          // significam nada na aba seguinte. A seleção de host, sim.
          setFiltrosCol({}); setKpiFiltro(null);
          setPagina(0);
        }}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          borderBottom: '1px solid #e2e8f0', minHeight: 40, bgcolor: '#f1f5f9',
          '& .MuiTab-root': { color: '#475569' },
          '& .Mui-selected': { color: '#b91c1c' },
        }}
      >
        {tabs.map((t) => (
          <Tab key={t.key} label={`${t.title} (${t.total})`} sx={{ minHeight: 40, py: 0 }} />
        ))}
      </Tabs>

      <TableSummary summary={tab.summary} filtroAtivo={kpiFiltro} onFiltrar={setKpiFiltro} />

      <Stack direction="row" spacing={1} sx={{ px: 1, pb: 1, pt: 1, bgcolor: '#f8fafc' }}>
        <Autocomplete
          multiple
          disableCloseOnSelect
          size="small"
          options={todosHosts}
          value={hostsSel}
          onChange={(_, v) => setHostsSel(v)}
          sx={{ width: 260, flex: '0 0 auto' }}
          renderTags={() => null}
          renderInput={(params) => (
            <TextField {...params}
              label={hostsSel.length ? `hosts (${hostsSel.length})` : 'hosts'}
              placeholder={hostsSel.length ? '' : 'todos'} />
          )}
        />
        <TextField
          size="small"
          fullWidth
          placeholder="buscar em qualquer coluna (Enter)…"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') setBuscaAplicada(busca); }}
          onBlur={() => setBuscaAplicada(busca)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
            ),
          }}
        />
        {temFiltro && (
          <Button size="small" startIcon={<ClearIcon fontSize="small" />}
                  onClick={limparTudo} sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
            limpar
          </Button>
        )}
      </Stack>

      {hostsSel.length > 0 && (
        <Box sx={{
          display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5,
          px: 1, pb: 1, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0',
          maxHeight: 84, overflowY: 'auto', flexShrink: 0,
        }}>
          {(verTodosHosts ? hostsSel : hostsSel.slice(0, MAX_CHIPS)).map((h) => (
            <Chip key={h} size="small" label={h}
                  onDelete={() => setHostsSel(hostsSel.filter((x) => x !== h))}
                  sx={{ bgcolor: '#e2e8f0', color: '#0f172a', maxWidth: 260 }} />
          ))}
          {hostsSel.length > MAX_CHIPS && (
            <Button size="small" onClick={() => setVerTodosHosts(!verTodosHosts)}
                    sx={{ minWidth: 0, px: 1 }}>
              {verTodosHosts ? 'ver menos' : `+${hostsSel.length - MAX_CHIPS}`}
            </Button>
          )}
        </Box>
      )}

      <TableContainer sx={{ flex: 1, overflow: 'auto', position: 'relative' }}>
        {carregandoPag && (
          <Box sx={{
            position: 'absolute', inset: 0, zIndex: 5, display: 'flex',
            alignItems: 'flex-start', justifyContent: 'center', pt: 8,
            bgcolor: 'rgba(255,255,255,0.6)',
          }}>
            <CircularProgress size={22} />
          </Box>
        )}
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {tab.cols.map((c, i) => (
                <TableCell key={i} sx={{
                  fontWeight: 700, whiteSpace: 'nowrap', bgcolor: '#f1f5f9',
                  color: '#0f172a', textTransform: 'uppercase', fontSize: 11,
                  letterSpacing: 0.4, borderBottom: 0, pb: 0,
                  ...(i === 0 ? { position: 'sticky', left: 0, zIndex: 3 } : null),
                }}>
                  <TableSortLabel
                    active={sortCol === i}
                    direction={sortCol === i ? sortDir : 'asc'}
                    onClick={() => onSort(i)}
                  >
                    {c}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
            {/* Segunda fileira do cabeçalho: um filtro por coluna. É mais
                eficiente que a busca global porque diz ONDE procurar — e nas
                colunas de valor repetido mostra o que existe em vez de fazer
                adivinhar. */}
            <TableRow>
              {tab.cols.map((c, i) => (
                <TableCell key={i} sx={{
                  bgcolor: '#f1f5f9', borderBottom: '2px solid #cbd5e1',
                  py: 0.25, px: 0.5,
                  ...(i === 0 ? { position: 'sticky', left: 0, zIndex: 3 } : null),
                }}>
                  <ColumnFilter
                    col={i}
                    label={c}
                    faceta={(tab.facetas || []).find((f) => f.col === i) || { tipo: 'texto' }}
                    valor={filtrosCol[i] || null}
                    onChange={(f) => setFiltrosCol((prev) => ({ ...prev, [i]: f }))}
                    onBuscarFaceta={buscarFaceta}
                  />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, ri) => (
              <TableRow key={ri} hover sx={{
                cursor: 'pointer',
                '&:nth-of-type(odd)': { bgcolor: '#f8fafc' },
                '&:nth-of-type(odd) td:first-of-type': { bgcolor: '#f8fafc' },
              }} onClick={() => setRowSel(row)}>
                {row.map((c, ci) => {
                  const num = isNum(c) && typeof c !== 'boolean';
                  const ehVazio = vazio(c);
                  return (
                    <TableCell key={ci} align={num ? 'right' : 'left'} sx={{
                      whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums',
                      fontFamily: num ? 'monospace' : undefined,
                      color: ehVazio ? '#94a3b8' : '#0f172a',
                      maxWidth: 340, overflow: 'hidden', textOverflow: 'ellipsis',
                      ...(ci === 0 ? {
                        position: 'sticky', left: 0, zIndex: 2,
                        bgcolor: '#ffffff', fontWeight: 600,
                      } : null),
                    }} title={ehVazio ? '' : String(c)}>
                      {ehVazio ? '—' : String(c)}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
            {!rows.length && !carregandoPag && (
              <TableRow>
                <TableCell colSpan={tab.cols.length}
                           sx={{ textAlign: 'center', color: '#94a3b8', py: 3 }}>
                  Nenhuma linha com os filtros atuais.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={pagina}
        onPageChange={(_, p) => setPagina(p)}
        rowsPerPage={porPagina}
        onRowsPerPageChange={(e) => { setPorPagina(parseInt(e.target.value, 10)); setPagina(0); }}
        rowsPerPageOptions={[50, 100, 250, 500]}
        labelRowsPerPage="linhas"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}–${to} de ${count}${temFiltro ? ` (${tab.total} sem filtro)` : ''}`}
        sx={{ borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc', minHeight: 40 }}
      />

      <HostDossier
        aberto={!!rowSel}
        onFechar={() => setRowSel(null)}
        host={rowSel && tab.host_col === 0 ? String(rowSel[0]) : null}
        vlan={rowSel && tab.key === 'vlans' ? String(rowSel[0]) : null}
        tabs={tabs}
        base={base}
        onFiltrarHost={(h) => setHostsSel((prev) => (prev.includes(h) ? prev : [...prev, h]))}
      />
    </Paper>
  );
}
